import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { tokenIsExpired } from "../../authUtils";
import { fetchData, deleteData } from "../../apiService";
import CategorySalesChart from "./CategorySalesChart";
import SalesPaymentStatusChart from "./piechart";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Card, CardContent, Typography, Box, IconButton, Modal,
  Table, TableBody, TableCell, TableRow, TableHead, CircularProgress,
  Tooltip, Divider,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Receipt, Inventory } from "@mui/icons-material";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import NoteIcon from "@mui/icons-material/Note";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import SummarizeIcon from "@mui/icons-material/Summarize";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BalanceIcon from "@mui/icons-material/Balance";
import ArticleIcon from "@mui/icons-material/Article";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";

const C = {
  accent: "#2dd4bf", accentDark: "#14b8a6",
  accentDim: "rgba(45,212,191,0.10)", accentBorder: "rgba(45,212,191,0.22)",
  bg: "#f0f4f4", card: "#ffffff", cardBorder: "rgba(0,0,0,0.06)",
  text: "#1a2e2e", muted: "#5a7a7a", shadow: "0 2px 10px rgba(0,0,0,0.07)",
};

const StatCard = ({ label, value, sub, icon, color, link, loading, trend }) => (
  <Link to={link || "#"} style={{ textDecoration: "none", flex: 1, minWidth: 140 }}>
    <Box sx={{
      bgcolor: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: "14px",
      boxShadow: C.shadow, p: "12px 16px", display: "flex", alignItems: "center",
      gap: 1.5, transition: "all 0.18s", cursor: "pointer", position: "relative", overflow: "hidden",
      "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", borderColor: color },
    }}>
      <Box sx={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", bgcolor: color, borderRadius: "14px 0 0 14px" }} />
      <Box sx={{ width: 42, height: 42, borderRadius: "12px", flexShrink: 0, bgcolor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Box sx={{ color, fontSize: 22, display: "flex" }}>{icon}</Box>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.6px", lineHeight: 1.2 }}>{label}</Typography>
        {loading ? <CircularProgress size={14} sx={{ color, mt: 0.3 }} /> : (
          <Typography sx={{ fontSize: "17px", fontWeight: 800, color: C.text, lineHeight: 1.3, fontFamily: "Inter, sans-serif" }}>{value ?? "—"}</Typography>
        )}
        {sub && <Typography sx={{ fontSize: "10px", color: C.muted, mt: 0.2 }}>{sub}</Typography>}
      </Box>
      {trend != null && !loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          {trend >= 0 ? <TrendingUpIcon sx={{ fontSize: 16, color: "#22c55e" }} /> : <TrendingDownIcon sx={{ fontSize: 16, color: "#ef4444" }} />}
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: trend >= 0 ? "#22c55e" : "#ef4444" }}>{Math.abs(trend)}%</Typography>
        </Box>
      )}
    </Box>
  </Link>
);

const QBtn = ({ icon, label, to, color }) => (
  <Link to={to} style={{ textDecoration: "none" }}>
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1, px: 1.4, py: 0.9, borderRadius: "9px",
      bgcolor: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow, cursor: "pointer",
      transition: "all 0.18s",
      "&:hover": { bgcolor: C.accentDim, borderColor: C.accentBorder, transform: "translateY(-1px)" },
    }}>
      <Box sx={{ color, display: "flex", alignItems: "center", fontSize: 18 }}>{icon}</Box>
      <Typography sx={{ fontSize: "11.5px", fontWeight: 600, color: C.text, fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{label}</Typography>
    </Box>
  </Link>
);

const groupColors = {
  Accounts: { pill: "#ede9fe", text: "#6d28d9", border: "#c4b5fd" },
  Sales:    { pill: "#dcfce7", text: "#15803d", border: "#86efac" },
  Purchase: { pill: "#fff7ed", text: "#c2410c", border: "#fdba74" },
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function getDaysInMonth(month, year) {
  const days = [];
  const firstDay = new Date(year, month, 1).getDay();
  const lastDate = new Date(year, month + 1, 0).getDate();
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= lastDate; d++) days.push(new Date(year, month, d));
  return days;
}

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const today = new Date();
const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
const todayStr = fmt(today);
const monthStartStr = fmt(monthStart);

const Home = () => {
  const { companyId, token, logout, isAuthenticated } = useAuth();

  // ── Stats state ──
  const [stats, setStats] = useState({
    todaySale: null, monthSale: null,
    todayPurchase: null, monthPurchase: null,
    todayReceipt: null, monthReceipt: null,
    todayPayment: null, monthPayment: null,
    totalCustomers: null, totalSuppliers: null,
    totalItems: null, stockValue: null,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // ── Recent transactions ──
  const [recentSales, setRecentSales] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);

  // ── Monthly trend chart ──
  const [trendData, setTrendData] = useState([]);

  // ── Calendar ──
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const calDays = getDaysInMonth(calMonth, calYear);

  // ── Refresh ──
  const [refreshKey, setRefreshKey] = useState(0);

  const fmtCur = (v) =>
    v == null ? "—" : "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  useEffect(() => {
    if (!companyId || !token) return;

    const load = async () => {
      setLoadingStats(true);
      setLoadingTx(true);
      try {
        // Today & month date strings
        const t = todayStr;
        const ms = monthStartStr;

        // Fetch sale summary for today
        const [saleTodayRes, saleMonthRes, purTodayRes, purMonthRes,
               rcptTodayRes, rcptMonthRes, payTodayRes, payMonthRes,
               masterRes, itemRes] = await Promise.allSettled([
          fetchData("SaleApi",     `/SaleDetails/${companyId}/${companyId}/${t}/${t}`, token),
          fetchData("SaleApi",     `/SaleDetails/${companyId}/${companyId}/${ms}/${t}`, token),
          fetchData("PurchaseApi", `/PurchaseDetails/${companyId}/${ms}/${t}`, token),
          fetchData("PurchaseApi", `/PurchaseDetails/${companyId}/${ms}/${t}`, token),
          fetchData("ReceiptApi",  `/ReceiptDetails/${companyId}/${t}/${t}`, token),
          fetchData("ReceiptApi",  `/ReceiptDetails/${companyId}/${ms}/${t}`, token),
          fetchData("PaymentApi",  `/PaymentDetails/${companyId}/${t}/${t}`, token),
          fetchData("PaymentApi",  `/PaymentDetails/${companyId}/${ms}/${t}`, token),
          fetchData("MasterApi",   `/MasterIndex/${companyId}`, token),
          fetchData("ItemApi",     `/ItemIndex/${companyId}`, token),
        ]);

        const sum = (res, field) => {
          if (res.status !== "fulfilled" || res.value?.responseCode !== "1111") return null;
          return res.value.response_data.reduce((a, r) => a + (Number(r[field]) || 0), 0);
        };
        const count = (res) => {
          if (res.status !== "fulfilled" || res.value?.responseCode !== "1111") return null;
          return res.value.response_data.length;
        };

        // Recent transactions (top 5)
        if (saleTodayRes.status === "fulfilled" && saleTodayRes.value?.responseCode === "1111") {
          setRecentSales(saleTodayRes.value.response_data.slice(0, 5));
        }
        if (saleMonthRes.status === "fulfilled" && saleMonthRes.value?.responseCode === "1111") {
          // Build monthly trend from month sales
          const byDay = {};
          saleMonthRes.value.response_data.forEach(r => {
            const d = r.BillDate?.split("T")[0] || r.BillDate;
            if (!byDay[d]) byDay[d] = { date: d, Sale: 0, Purchase: 0 };
            byDay[d].Sale += Number(r.GrandTotal) || 0;
          });
          if (purTodayRes.status === "fulfilled" && purTodayRes.value?.responseCode === "1111") {
            purTodayRes.value.response_data.forEach(r => {
              const d = r.BillDate?.split("T")[0] || r.BillDate;
              if (!byDay[d]) byDay[d] = { date: d, Sale: 0, Purchase: 0 };
              byDay[d].Purchase += Number(r.TotalAmount) || 0;
            });
          }
          const trend = Object.values(byDay)
            .sort((a, b) => a.date.localeCompare(b.date))
            .map(r => ({ ...r, date: r.date.slice(5) })); // MM-DD
          setTrendData(trend);
        }
        if (purTodayRes.status === "fulfilled" && purTodayRes.value?.responseCode === "1111") {
          setRecentPurchases(purTodayRes.value.response_data.slice(0, 5));
        }

        // Customers / Suppliers from master
        let customers = null, suppliers = null;
        if (masterRes.status === "fulfilled" && masterRes.value?.responseCode === "1111") {
          const d = masterRes.value.response_data;
          customers = d.filter(r => r.MasterTypeName === "Customer" || r.MasterType === 1).length;
          suppliers = d.filter(r => r.MasterTypeName === "Supplier" || r.MasterType === 2).length;
        }

        setStats({
          todaySale:      sum(saleTodayRes,  "GrandTotal"),
          monthSale:      sum(saleMonthRes,  "GrandTotal"),
          todayPurchase:  sum(purTodayRes,   "TotalAmount"),
          monthPurchase:  sum(purMonthRes,   "TotalAmount"),
          todayReceipt:   sum(rcptTodayRes,  "Amount"),
          monthReceipt:   sum(rcptMonthRes,  "Amount"),
          todayPayment:   sum(payTodayRes,   "Amount"),
          monthPayment:   sum(payMonthRes,   "Amount"),
          totalCustomers: customers,
          totalSuppliers: suppliers,
          totalItems:     count(itemRes),
          stockValue:     null,
        });
      } catch (e) {
        console.error("Dashboard load error", e);
      } finally {
        setLoadingStats(false);
        setLoadingTx(false);
      }
    };

    load();
  }, [companyId, token, refreshKey]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";

  return (
    <Box sx={{ bgcolor: C.bg, p: "6px 6px 6px" }}>

      {/* ── Header row ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: "13px", color: C.text }}>
          Dashboard
        </Typography>
        <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
          <Typography sx={{ fontSize: "10px", color: C.muted }}>
            {MONTHS[today.getMonth()]} {today.getFullYear()}
          </Typography>
          <IconButton size="small" onClick={() => setRefreshKey(k => k + 1)}
            sx={{ bgcolor: C.accentDim, border: `1px solid ${C.accentBorder}`, borderRadius: "6px", p: "3px" }}>
            <RefreshIcon sx={{ fontSize: 13, color: C.accent }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Quick Actions ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mb: 1.2 }}>
        {[
          { icon: <StorefrontIcon />,    label: "New Sale",      to: "/SaleCreate",         color: "#2dd4bf" },
          { icon: <LocalShippingIcon />, label: "New Purchase",  to: "/PurchaseCreate",     color: "#6366f1" },
          { icon: <ReceiptLongIcon />,   label: "New Receipt",   to: "/ReceiptCreate",      color: "#22c55e" },
          { icon: <PaymentIcon />,       label: "New Payment",   to: "/PaymentCreate",      color: "#f59e0b" },
          { icon: <NoteIcon />,          label: "Journal Entry", to: "/JournelEntry",       color: "#ec4899" },
          { icon: <SummarizeIcon />,     label: "Sale Register", to: "/SaleRegister",       color: "#14b8a6" },
          { icon: <ShowChartIcon />,     label: "Trial Balance", to: "/TrialBalanceReport", color: "#8b5cf6" },
          { icon: <BalanceIcon />,       label: "Balance Sheet", to: "/BalanceSheetIndex",  color: "#0ea5e9" },
        ].map(({ icon, label, to, color }) => (
          <Link key={to} to={to} style={{ textDecoration: "none" }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 0.6, px: 1, py: 0.5, borderRadius: "7px",
              bgcolor: C.card, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow, cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { bgcolor: C.accentDim, borderColor: C.accentBorder, transform: "translateY(-1px)" },
            }}>
              <Box sx={{ color, display: "flex", alignItems: "center", fontSize: 14 }}>{icon}</Box>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: C.text, fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{label}</Typography>
            </Box>
          </Link>
        ))}
      </Box>



      {/* ── Charts row ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 1.2 }}>

        {/* Trend Chart */}
        <Box sx={{ flex: "2 1 300px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px 6px" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
            Sale vs Purchase — This Month
          </Typography>
          {trendData.length === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120 }}>
              <CircularProgress size={20} sx={{ color: C.accent }} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={trendData} margin={{ top: 2, right: 6, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 8, fill: C.muted }} />
                <YAxis tick={{ fontSize: 8, fill: C.muted }} />
                <RechartTooltip contentStyle={{ fontSize: 10, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 9 }} />
                <Line type="monotone" dataKey="Sale"     stroke="#2dd4bf" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="Purchase" stroke="#6366f1" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Category Sales Chart */}
        <Box sx={{ flex: "1 1 220px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px 6px", overflow: "hidden" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
            Category-wise Sales
          </Typography>
          <CategorySalesChart />
        </Box>

        {/* Payment Status Pie */}
        <Box sx={{ flex: "1 1 180px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px 6px", overflow: "hidden" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
            Payment Status
          </Typography>
          <SalesPaymentStatusChart />
        </Box>
      </Box>

      {/* ── Recent Transactions + Calendar ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2, mb: 1.2 }}>

        {/* Recent Sales */}
        <Box sx={{ flex: "1 1 240px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Today's Sales
            </Typography>
            <Link to="/SaleIndex" style={{ fontSize: "9px", color: C.accent, textDecoration: "none", display: "flex", alignItems: "center", gap: "2px" }}>
              View all <OpenInNewIcon sx={{ fontSize: 10 }} />
            </Link>
          </Box>
          {loadingTx ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}><CircularProgress size={16} sx={{ color: C.accent }} /></Box>
          ) : recentSales.length === 0 ? (
            <Typography sx={{ fontSize: "10px", color: C.muted, textAlign: "center", py: 1.5 }}>No sales today</Typography>
          ) : (
            <Box>
              {recentSales.map((s, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "3px", borderBottom: i < recentSales.length - 1 ? `1px solid ${C.cardBorder}` : "none" }}>
                  <Box>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: C.text }}>{s.CustomerName || "—"}</Typography>
                    <Typography sx={{ fontSize: "9px", color: C.muted }}>{s.BillNumber} · {fmtDate(s.BillDate)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#2dd4bf" }}>₹{Number(s.GrandTotal || 0).toLocaleString("en-IN")}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Recent Purchases */}
        <Box sx={{ flex: "1 1 240px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.8 }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Recent Purchases
            </Typography>
            <Link to="/PurchaseIndex" style={{ fontSize: "9px", color: C.accent, textDecoration: "none", display: "flex", alignItems: "center", gap: "2px" }}>
              View all <OpenInNewIcon sx={{ fontSize: 10 }} />
            </Link>
          </Box>
          {loadingTx ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}><CircularProgress size={16} sx={{ color: C.accent }} /></Box>
          ) : recentPurchases.length === 0 ? (
            <Typography sx={{ fontSize: "10px", color: C.muted, textAlign: "center", py: 1.5 }}>No purchases today</Typography>
          ) : (
            <Box>
              {recentPurchases.map((p, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "3px", borderBottom: i < recentPurchases.length - 1 ? `1px solid ${C.cardBorder}` : "none" }}>
                  <Box>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: C.text }}>{p.SupplierName || "—"}</Typography>
                    <Typography sx={{ fontSize: "9px", color: C.muted }}>{p.BillNumber} · {fmtDate(p.BillDate)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#6366f1" }}>₹{Number(p.TotalAmount || 0).toLocaleString("en-IN")}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Mini Calendar */}
        <Box sx={{ flex: "0 1 210px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <IconButton size="small" onClick={() => {
              if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
              else setCalMonth(m => m - 1);
            }} sx={{ p: "1px" }}>
              <ArrowBackIosIcon sx={{ fontSize: 10, color: C.muted }} />
            </IconButton>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.text }}>
              {MONTHS[calMonth]} {calYear}
            </Typography>
            <IconButton size="small" onClick={() => {
              if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
              else setCalMonth(m => m + 1);
            }} sx={{ p: "1px" }}>
              <ArrowForwardIosIcon sx={{ fontSize: 10, color: C.muted }} />
            </IconButton>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "1px", textAlign: "center" }}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <Typography key={d} sx={{ fontSize: "8px", fontWeight: 700, color: C.muted, py: "1px" }}>{d}</Typography>
            ))}
            {calDays.map((d, i) => {
              const isToday = d && fmt(d) === todayStr;
              return (
                <Box key={i} sx={{
                  fontSize: "9px", py: "2px", borderRadius: "4px",
                  bgcolor: isToday ? C.accent : "transparent",
                  color: isToday ? "#fff" : d ? C.text : "transparent",
                  fontWeight: isToday ? 800 : 400,
                }}>
                  {d ? d.getDate() : ""}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* ── Navigation Groups ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
        {[
          {
            group: "Accounts",
            links: [
              { label: "Journal Entry",    to: "/JournelEntryIndex",         icon: <NoteIcon /> },
              { label: "Receipt",          to: "/ReceiptIndex",              icon: <ReceiptLongIcon /> },
              { label: "Payment",          to: "/PaymentIndex",              icon: <PaymentIcon /> },
              { label: "Ledger",           to: "/Ledger",                    icon: <SummarizeIcon /> },
              { label: "Trial Balance",    to: "/TrialBalanceReport",        icon: <BalanceIcon /> },
              { label: "Balance Sheet",    to: "/BalanceSheetIndex",         icon: <AttachMoneyIcon /> },
              { label: "Cash Book",        to: "/CashBook",                  icon: <AccountBalanceWalletIcon /> },
            ],
          },
          {
            group: "Sales",
            links: [
              { label: "Sale",             to: "/SaleIndex",                 icon: <StorefrontIcon /> },
              { label: "Sale Return",      to: "/SaleReturnIndex",           icon: <ArticleIcon /> },
              { label: "Proforma",         to: "/ProformaIndex",             icon: <TextSnippetIcon /> },
              { label: "Sale Register",    to: "/SaleRegister",              icon: <ShowChartIcon /> },
              { label: "GST Report",       to: "/Salegstreport",             icon: <SummarizeIcon /> },
              { label: "Customer O/S",     to: "/CustomerOutstandingReport", icon: <PeopleAltIcon /> },
            ],
          },
          {
            group: "Purchase",
            links: [
              { label: "Purchase",         to: "/PurchaseIndex",             icon: <LocalShippingIcon /> },
              { label: "Purchase Return",  to: "/PurchaseReturnIndex",       icon: <ArticleIcon /> },
              { label: "Purchase Order",   to: "/PurchaseOrderIndex",        icon: <TextSnippetIcon /> },
              { label: "Purchase Register",to: "/PurchaseRegisterReport",    icon: <ShowChartIcon /> },
              { label: "Supplier O/S",     to: "/SupplierOutstandingReport", icon: <SupervisedUserCircleIcon /> },
              { label: "Stock",            to: "/Stock",                     icon: <PermIdentityIcon /> },
            ],
          },
        ].map(({ group, links }) => {
          const gc = groupColors[group];
          return (
            <Box key={group} sx={{ flex: "1 1 180px", bgcolor: C.card, borderRadius: "10px", boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, p: "10px 12px" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", px: 1, py: 0.2, borderRadius: "20px", bgcolor: gc.pill, border: `1px solid ${gc.border}`, mb: 1 }}>
                <Typography sx={{ fontSize: "9px", fontWeight: 700, color: gc.text, textTransform: "uppercase", letterSpacing: "0.5px" }}>{group}</Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
                {links.map(({ label, to, icon }) => (
                  <Link key={to} to={to} style={{ textDecoration: "none" }}>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 0.8, px: 0.8, py: "3px", borderRadius: "6px",
                      transition: "all 0.12s",
                      "&:hover": { bgcolor: C.accentDim },
                    }}>
                      <Box sx={{ color: gc.text, fontSize: 13, display: "flex" }}>{icon}</Box>
                      <Typography sx={{ fontSize: "10.5px", color: C.text, fontFamily: "Inter, sans-serif" }}>{label}</Typography>
                    </Box>
                  </Link>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>

    </Box>
  );
};

export default Home;

